import { BasicColumnTypes, IOgqlColumn, SemanticTypes } from '@opengov/ogql-types';
import { IDatasetLensColumn, ECalendarSemanticTypes } from '@opengov/reporting-types';
import {
  createAggregateReportColumn,
  createConditionalReportColumn,
  createConstantReportColumn,
  createInfixReportColumn,
  createPercentOfTotalReportColumn,
  createReportColumnMetaData,
  createReportColumnMetaDataTags,
  createStorageReportColumn,
  EReportColumnTypes
} from '@opengov/reporting-types/src/reportColumns-immer';

export const calculateColumnType = (column: IOgqlColumn) => {
  const semanticTypes = column.semanticTypes;
  if (semanticTypes && column.type === BasicColumnTypes.Numeric) {
    if (
      semanticTypes.find((t) => {
        return t.type.includes(BasicColumnTypes.Percent);
      })
    ) {
      return BasicColumnTypes.Percent;
    }
    if (semanticTypes.find((t) => t.type.includes(BasicColumnTypes.Currency))) {
      return BasicColumnTypes.Currency;
    }
    return BasicColumnTypes.Numeric;
  }
  if (column.type === BasicColumnTypes.Text || column.type === 'citext') {
    return BasicColumnTypes.Text;
  }
  if (
    column.type === BasicColumnTypes.Period &&
    semanticTypes &&
    semanticTypes.find((t) => t.type.includes(SemanticTypes.Period.FiscalYear))
  ) {
    return BasicColumnTypes.Year;
  }
  if (column.type === BasicColumnTypes.Date) {
    return BasicColumnTypes.Date;
  }
  return column.type;
};

export const lensColumnSemanticTypeToColumnType = (column: IDatasetLensColumn) => {
  const semanticTypes = column.semanticTypes;
  if (semanticTypes && column.type.includes('logical/numeric')) {
    if (
      semanticTypes.find((t) => {
        return t.includes(BasicColumnTypes.Percent);
      })
    ) {
      return BasicColumnTypes.Percent;
    }
    if (semanticTypes.includes(ECalendarSemanticTypes.FISCAL_YEAR)) {
      return BasicColumnTypes.Year;
    }
    if (semanticTypes.includes(ECalendarSemanticTypes.FISCAL_PERIOD)) {
      return BasicColumnTypes.Period;
    }
    if (
      semanticTypes.find((t) => t.includes(BasicColumnTypes.Currency)) ||
      semanticTypes.find((type) => type === SemanticTypes.StructuredDataType.MonetaryAmount || type === SemanticTypes.StructuredDataType.MonetaryAmountHttps)
    ) {
      return BasicColumnTypes.Currency;
    }
    return BasicColumnTypes.Numeric;
  }
  if (column.type.includes('logical/text') || column.type.includes('logical/caseinsensitivetext')) {
    return BasicColumnTypes.Text;
  }
  if (column.type.includes('logical/bool')) {
    return BasicColumnTypes.Boolean;
  }
  if (column.type.includes('fiscal/period')) {
    return BasicColumnTypes.Year;
  }
  if (column.type.includes('logical/date')) {
    return BasicColumnTypes.Date;
  }
  return column.type as BasicColumnTypes;
};

export const lensColumnToStorageReportColumn = (column: IDatasetLensColumn) => {
  return createStorageReportColumn({
    ...column,
    type: lensColumnSemanticTypeToColumnType(column),
    displayName: column.label,
    hidden: !column.visible,
    semanticTypes: column.semanticTypes,
    metadata: createReportColumnMetaData({
      tags: createReportColumnMetaDataTags({
        hidden: !column.visible
      })
    })
  });
};

export const lensColumnsToStorageReportColumns = (columns: IDatasetLensColumn[]) =>
  columns.map(lensColumnToStorageReportColumn);

export const columnTypeToFactory = (column) => {
  switch (column.columnType) {
    case EReportColumnTypes.aggregate:
      return createAggregateReportColumn(column);
    case EReportColumnTypes.conditional:
      return createConditionalReportColumn(column);
    case EReportColumnTypes.constant:
      return createConstantReportColumn(column);
    case EReportColumnTypes.infix:
      return createInfixReportColumn(column);
    case EReportColumnTypes.percentOfTotal:
      return createPercentOfTotalReportColumn(column);
    case EReportColumnTypes.storage:
    default:
      return createStorageReportColumn(column);
  }
};
